import { supabase } from '../lib/supabase';

export type PlatformPurchaseStatus = 'pending' | 'succeeded' | 'failed' | 'cancelled' | 'refunded';

export type PlatformPurchaseAction =
  | string
  | {
      type:
        | 'visibility_boost'
        | 'hochschieben_once'
        | 'highlight_7d'
        | 'hochschieben_daily_7d'
        | 'top_anzeige_7d'
        | 'galerie_startseite_10d'
        | 'custom';
      label?: string;
      details?: string;
      duration_days?: number;
    };

export interface PlatformPurchaseItem {
  id: string;
  title?: string;
  description?: string;
  price?: number;
  currency?: string;
  badge?: string;
  recommended?: boolean;
  action?: PlatformPurchaseAction;
}

export interface CreatePlatformPurchaseInput {
  productId?: string | null;
  currency: string;
  totalAmount: number;
  items: PlatformPurchaseItem[];
  status?: PlatformPurchaseStatus;
  provider?: 'paypal' | 'manual' | string;
  providerOrderId?: string | null;
  providerPayload?: any;
  completedAt?: string | null;
}

export interface PlatformPurchaseRecord {
  id: string;
  purchaser_id: string;
  product_id: string | null;
  product?: {
    id: string;
    name: string;
    price: number;
    images: string[];
    is_job?: boolean;
  } | null;
  provider: string;
  provider_order_id: string | null;
  currency: string;
  total_amount: number;
  items: PlatformPurchaseItem[];
  status: PlatformPurchaseStatus;
  completed_at: string | null;
  cancelled_at: string | null;
  provider_payload: any;
  created_at: string;
  updated_at: string;
}

export class PlatformPurchasesService {
  static async create(input: CreatePlatformPurchaseInput): Promise<any> {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) throw new Error('User must be authenticated');

    const payload = {
      purchaser_id: user.id,
      product_id: input.productId ?? null,
      provider: input.provider ?? 'paypal',
      provider_order_id: input.providerOrderId ?? null,
      currency: (input.currency || 'USD').toUpperCase(),
      total_amount: Number(input.totalAmount) || 0,
      items: Array.isArray(input.items) ? input.items : [],
      status: input.status ?? 'succeeded',
      provider_payload: input.providerPayload ?? null,
      completed_at: input.completedAt ?? (input.status === 'succeeded' ? new Date().toISOString() : null),
    };

    const { data, error } = await supabase
      .from('platform_purchases')
      .insert(payload)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  static async listMyPurchases(params?: { limit?: number }): Promise<PlatformPurchaseRecord[]> {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) throw new Error('User must be authenticated');

    let q = supabase
      .from('platform_purchases')
      .select('*, product:products(id,name,price,images,is_job)')
      .eq('purchaser_id', user.id)
      .order('created_at', { ascending: false });

    if (params?.limit && params.limit > 0) {
      q = q.limit(params.limit);
    }

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data || []) as PlatformPurchaseRecord[];
  }

  static async listMyPurchasesPage(params: {
    page: number;
    pageSize: number;
  }): Promise<{ rows: PlatformPurchaseRecord[]; total: number }> {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) throw new Error('User must be authenticated');

    const page = Math.max(0, Number(params.page) || 0);
    const pageSize = Math.min(50, Math.max(1, Number(params.pageSize) || 10));
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('platform_purchases')
      .select('*, product:products(id,name,price,images,is_job)', { count: 'exact' })
      .eq('purchaser_id', user.id)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw new Error(error.message);
    return {
      rows: (data || []) as PlatformPurchaseRecord[],
      total: Number(count || 0),
    };
  }
}
